import { useState, useCallback, useId } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FOREST_GREEN, MID_GRAY, ERROR, WARNING, WHITE } from '@/styles/tokens';
import type { QualityGateBlock } from '@/types/plan';

function verdictColor(verdict: 'pass' | 'warn' | 'fail'): string {
  switch (verdict) {
    case 'pass': return FOREST_GREEN.DEFAULT;
    case 'warn': return WARNING.DEFAULT;
    case 'fail': return ERROR.DEFAULT;
  }
}

interface PlanQualityGateDialogProps {
  gate: QualityGateBlock;
  onResolve: (action: 'proceed' | 'revise' | 'skip' | 'discard') => void;
}

export function PlanQualityGateDialog({ gate, onResolve }: PlanQualityGateDialogProps) {
  const [disabled, setDisabled] = useState(false);
  const titleId = useId();
  const descId = useId();

  const handleAction = useCallback(
    (action: 'proceed' | 'revise' | 'skip' | 'discard') => {
      if (disabled) return;
      setDisabled(true);
      setTimeout(() => setDisabled(false), 300);
      onResolve(action);
    },
    [disabled, onResolve],
  );

  const { auditResult } = gate;
  const issues = [
    ...auditResult.blockingIssues.map((issue) => ({ text: issue, severity: 'blocking' as const })),
    ...auditResult.warnings.map((w) => ({ text: w, severity: 'warning' as const })),
    ...auditResult.suggestions.map((s) => ({ text: s, severity: 'suggestion' as const })),
  ];
  const weakCriteria = (auditResult.criteria || []).filter((c) => c.score < 95);

  const color = verdictColor(auditResult.verdict);

  return (
    <Dialog open={true}>
      <DialogContent
        variant="light"
        className="max-w-lg"
        aria-labelledby={titleId}
        aria-describedby={descId}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Escape') handleAction('discard');
          if (e.key === 'Enter') handleAction('proceed');
        }}
      >
        <DialogHeader>
          <DialogTitle id={titleId} className="flex items-center gap-2" style={{ fontSize: 16 }}>
            <AlertTriangle className="w-5 h-5" style={{ color: WARNING.DEFAULT }} />
            Quality Gate
          </DialogTitle>
        </DialogHeader>

        <div id={descId} style={{ padding: '0 24px 16px' }}>
          <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
            The {gate.auditResult.auditType.replace('_', ' ')} audit returned a score of{' '}
            <span style={{ fontWeight: 600, color }}>{auditResult.overallScore}/100</span>{' '}
            (<span style={{ textTransform: 'uppercase', fontWeight: 600, color }}>{auditResult.verdict}</span>).
          </p>

          {issues.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Key Issues:</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {issues.map((issue, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: issue.severity === 'blocking' ? ERROR.DEFAULT : issue.severity === 'warning' ? WARNING.DEFAULT : MID_GRAY.DEFAULT,
                      marginBottom: 4,
                    }}
                  >
                    {issue.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {weakCriteria.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Criteria Scores:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {weakCriteria.map((c, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 12,
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: c.score < 80 ? `${ERROR.DEFAULT}15` : `${WARNING.DEFAULT}15`,
                      color: c.score < 80 ? ERROR.DEFAULT : WARNING.DEFAULT,
                      fontWeight: 500,
                    }}
                  >
                    {c.name}: {c.score}/100
                  </span>
                ))}
              </div>
            </div>
          )}

          <p style={{ fontSize: 14, color: MID_GRAY.DEFAULT, marginBottom: 20 }}>
            What would you like to do?
          </p>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleAction('proceed')}
              aria-label="Proceed despite warnings"
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: WARNING.DEFAULT,
                color: WHITE,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Proceed Anyway
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleAction('revise')}
              aria-label="Return to revise the plan"
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: `1px solid ${FOREST_GREEN.DEFAULT}`,
                background: 'transparent',
                color: FOREST_GREEN.DEFAULT,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Revise
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleAction('skip')}
              aria-label="Skip this quality gate"
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: MID_GRAY.DEFAULT,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Skip
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => handleAction('discard')}
              aria-label="Discard the entire plan"
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                color: ERROR.DEFAULT,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Discard Plan
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
